import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Autocomplete,
  CircularProgress,
  Typography,
} from '@mui/material';
import { trainersAPI, vetsAPI, mareOwnersAPI } from '../services/api';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const defaultForm = {
  name: '',
  description: '',
  trainer_id: '',
  vet_id: '',
  mare_owner_id: '',
};

const AddLocationModal = ({ open, onClose, onSave }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [formState, setFormState] = useState(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [trainers, setTrainers] = useState([]);
  const [vets, setVets] = useState([]);
  const [mareOwners, setMareOwners] = useState([]);

  const hasReferenceOptions = useMemo(
    () => trainers.length > 0 || vets.length > 0 || mareOwners.length > 0,
    [trainers, vets, mareOwners]
  );

  const autocompleteComponentsProps = useMemo(
    () => ({
      popper: { disablePortal: true },
      paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 2 } },
    }),
    []
  );

  useEffect(() => {
    if (open) {
      loadReferenceOptions();
    } else {
      setFormState(defaultForm);
      setSubmitting(false);
      setLoadingOptions(false);
    }
  }, [open]);

  const loadReferenceOptions = async () => {
    setLoadingOptions(true);
    try {
      const [trainersRes, vetsRes, mareOwnersRes] = await Promise.allSettled([
        trainersAPI.getAll({ active: true, sort_by: 'business_name' }),
        vetsAPI.getAll({ active: true, sort_by: 'clinic_name' }),
        mareOwnersAPI.getAll({ active: 'true', sort_by: 'name' }),
      ]);

      if (trainersRes.status === 'fulfilled') {
        setTrainers(toArray(trainersRes.value.data, 'trainers'));
      } else {
        console.error('Failed to load trainers:', trainersRes.reason);
      }

      if (vetsRes.status === 'fulfilled') {
        setVets(toArray(vetsRes.value.data, 'vets'));
      } else {
        console.error('Failed to load vets:', vetsRes.reason);
      }

      if (mareOwnersRes.status === 'fulfilled') {
        setMareOwners(toArray(mareOwnersRes.value.data, 'mare owners'));
      } else {
        console.error('Failed to load mare owners:', mareOwnersRes.reason);
      }
    } catch (error) {
      console.error('Error loading reference options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleClose = () => {
    setFormState(defaultForm);
    setSubmitting(false);
    fields.reset();
    onClose();
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    fields.clear(name);
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!fields.validate({ name: !formState.name.trim() && 'Location name is required' })) return;

    setSubmitting(true);
    try {
      const payload = {
        name: formState.name.trim(),
        description: formState.description?.trim() || undefined,
        trainer_id: formState.trainer_id || undefined,
        vet_id: formState.vet_id || undefined,
        mare_owner_id: formState.mare_owner_id || undefined,
      };

      await onSave(payload);
      handleClose();
    } catch (error) {
      console.error('Error saving location:', error);
      notify.error(errorMessage(error, 'Error saving location'));
    } finally {
      setSubmitting(false);
    }
  };

  const getTrainerLabel = (trainer) => {
    if (!trainer) return '';
    if (trainer.business_name) return trainer.business_name;
    if (trainer.trainer_name) return trainer.trainer_name;
    return `Trainer #${trainer.id}`;
  };

  const getVetLabel = (vet) => {
    if (!vet) return '';
    if (vet.clinic_name) return vet.clinic_name;
    if (vet.vet_name) return vet.vet_name;
    return `Vet #${vet.id}`;
  };

  const getMareOwnerLabel = (owner) => {
    if (!owner) return '';
    return owner.name || `Mare Owner #${owner.id}`;
  };

  const trainerOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...trainers.map((trainer) => ({ id: trainer.id, label: getTrainerLabel(trainer) })),
  ], [trainers]);

  const vetOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...vets.map((vet) => ({ id: vet.id, label: getVetLabel(vet) })),
  ], [vets]);

  const mareOwnerOptions = useMemo(() => [
    { id: '', label: 'None' },
    ...mareOwners.map((owner) => ({ id: owner.id, label: getMareOwnerLabel(owner) })),
  ], [mareOwners]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      // Use a very high z-index to ensure it appears above all nested HorseModals
      sx={{ zIndex: 1400 }}
    >
      <DialogTitle>Add New Location</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
                  size="small"
            label="Location Name"
            name="name"
            value={formState.name}
            onChange={handleChange}
            {...fields.props('name')}
            required
            fullWidth
            autoFocus
          />
          <TextField
                  size="small"
            label="Description (Optional)"
            name="description"
            value={formState.description}
            onChange={handleChange}
            multiline
            rows={2}
            fullWidth
          />

          {loadingOptions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {!hasReferenceOptions ? (
                <Typography variant="body2" color="text.secondary">
                  No trainers, vets, or mare owners found. You can add them later and edit the location.
                </Typography>
              ) : (
                <>
                  <Autocomplete
                    size="small"
                    fullWidth
                    disableClearable
                    options={trainerOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={autocompleteComponentsProps}
                    value={trainerOptions.find((o) => o.id === formState.trainer_id) ?? trainerOptions[0]}
                    onChange={(e, newValue) =>
                      setFormState((prev) => ({ ...prev, trainer_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Trainer (Optional)" />}
                  />

                  <Autocomplete
                    size="small"
                    fullWidth
                    disableClearable
                    options={vetOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={autocompleteComponentsProps}
                    value={vetOptions.find((o) => o.id === formState.vet_id) ?? vetOptions[0]}
                    onChange={(e, newValue) =>
                      setFormState((prev) => ({ ...prev, vet_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Vet (Optional)" />}
                  />

                  <Autocomplete
                    size="small"
                    fullWidth
                    disableClearable
                    options={mareOwnerOptions}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                    filterOptions={createPrefixFilter((o) => o.label)}
                    componentsProps={autocompleteComponentsProps}
                    value={mareOwnerOptions.find((o) => o.id === formState.mare_owner_id) ?? mareOwnerOptions[0]}
                    onChange={(e, newValue) =>
                      setFormState((prev) => ({ ...prev, mare_owner_id: newValue?.id ?? '' }))
                    }
                    renderInput={(params) => <TextField {...params} label="Mare Owner (Optional)" />}
                  />
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loadingOptions}
        >
          {submitting ? 'Adding...' : 'Add Location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLocationModal;


